import { EmptyState, NoDataIcon, PageHeader } from '@lmc/ui-kit'
import { Button } from '@lmc/ui-kit/src/general'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { useIncidentsReportList } from '../../hooks'
import { FilterValues } from '../../hooks/useIncidentsReportList'
import { useTranslation } from 'react-i18next'
import { Home } from '@lmc/ts-common'
import { useSelector } from 'react-redux'
import { incidentReportsConstants } from 'locale/incidentReports'
import { NAMESPACE } from 'locale'
import useFilterParamsGeneric from 'hooks/useFilterParamsGeneric'
import { Spinner } from 'components/generic'
import usePdfExport from 'hooks/usePdfExport'
import logo from '../../../../../images/LMC-Logo-Pos-RGB 1.png'
import { FilePdfOutlined } from '@ant-design/icons'
import jsPDF from 'jspdf'
import { IncidentsCharts } from './IncidentsCharts'
import { IncidentFilters } from '../../components/IncidentFilters'

const initialFiltersValue: FilterValues = {
  rangePicker: [dayjs().subtract(4, 'week').startOf('day'), dayjs()],
  residents: [],
  locations: [],
  categoryItems: [],
  statuses: [],
  severities: [],
  resultedIn: [],
}

export type IncidentsReportMainProps = {
  title?: string
  description?: string
  hasResidentsFilter?: boolean
  hasHomesFilter?: boolean
  hasOnClick?: boolean
}

export type SetFilterParams = (f: (filterParams: FilterValues) => void) => void

export const IncidentsDashboard = ({
  description,
  hasResidentsFilter,
  hasHomesFilter,
}: IncidentsReportMainProps) => {
  const { filterParamsGeneric, setFilterParams } =
    useFilterParamsGeneric(initialFiltersValue)

  const filterParams = filterParamsGeneric as FilterValues

  const {
    data: incidents,
    isLoading,
    refetch,
  } = useIncidentsReportList(filterParams as FilterValues, 'dashboard')

  useEffect(() => {
    refetch()
  }, [])

  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const hasIncidents = !!(incidents && Object.keys(incidents).length)

  const [isDownloading, setIsDownloading] = useState(false)

  const generatePDFHeader = (pdf: jsPDF) => {
    pdf.setFont('Helvetica')
    pdf.setFontSize(24)
    pdf.text(`${t(incidentReportsConstants.INCIDENTS_DASHBOARD)} - PDF`, 10, 20)
    pdf.addImage(logo, 'JPEG', 170, 15, 140 / 4, 25 / 4)
    pdf.setDrawColor('#D9D9D9')
    pdf.setLineWidth(0.25)
    pdf.line(10, 25, 220, 25)
    pdf.setFontSize(18)
    pdf.text(`${t(incidentReportsConstants.SITE)}: ${home.name}`, 10, 35)
  }

  const pdfContent = useRef<HTMLInputElement>(null)
  const filtersRef = useRef<HTMLInputElement>(null)
  const handleDownloadPdf = usePdfExport(
    [filtersRef, pdfContent],
    `${t(incidentReportsConstants.INCIDENTS_DASHBOARD)}:${home.name}`,
    setIsDownloading,
    1,
    generatePDFHeader,
    35
  )

  return (
    <>
      <PageHeader
        title={
          <TitleContainer>
            {t(incidentReportsConstants.INCIDENTS_DASHBOARD)}
          </TitleContainer>
        }
        subtitle={description}
      >
        <Button onClick={handleDownloadPdf} isLoading={isDownloading}>
          <FilePdfOutlined />
          {t(incidentReportsConstants.DOWNLOAD_PDF)}
        </Button>
      </PageHeader>
      <IncidentFilters
        filterParams={filterParams}
        setFilterParams={setFilterParams as SetFilterParams}
        hasResidentsFilter={hasResidentsFilter}
        hasHomesFilter={hasHomesFilter}
      />
      {hasIncidents && (
        <IncidentsCharts filterParams={filterParams} ref={pdfContent} />
      )}
      {!isLoading && !hasIncidents && (
        <EmptyState
          title={t(incidentReportsConstants.NO_DATA)}
          icon={<NoDataIcon />}
        />
      )}
      {isLoading && <Spinner />}
    </>
  )
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`
